import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { listDocs, type DocSummary } from '../lib/collateral';
import { listSpecs, listRenders, renderLabel, type SpecSummary, type RenderFile } from '../lib/assets';

type Theme = 'light' | 'dark';

/** Everything you've made, in one durable place: rendered videos, one-pagers, and video projects. */
export default function AssetsPage() {
  const [theme, setTheme] = useState<Theme>(() =>
    (typeof localStorage !== 'undefined' && localStorage.getItem('studio-theme')) === 'dark' ? 'dark' : 'light',
  );
  const [docs, setDocs] = useState<DocSummary[]>([]);
  const [specs, setSpecs] = useState<SpecSummary[]>([]);
  const [renders, setRenders] = useState<RenderFile[]>([]);
  const nav = useNavigate();

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('studio-theme', theme);
  }, [theme]);

  useEffect(() => {
    listDocs().then(setDocs).catch(() => {});
    listSpecs().then(setSpecs).catch(() => {});
    listRenders().then(setRenders).catch(() => {});
  }, []);

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', color: 'var(--text)' }}>
      <header style={{ display: 'flex', alignItems: 'center', gap: '0.7rem', height: 56, padding: '0 1.4rem', borderBottom: '1px solid var(--border)', background: 'var(--surface)', position: 'sticky', top: 0, zIndex: 2 }}>
        <span className="font-display" style={{ fontSize: 19, fontWeight: 600, letterSpacing: '-0.01em' }}>Studio</span>
        <span style={{ color: 'var(--border-strong)' }}>/</span>
        <span className="font-mono" style={{ fontSize: 12, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--text-dim)' }}>Assets</span>
        <span style={{ flex: 1 }} />
        <Link to="/" className="btn" style={{ padding: '0.35rem 0.7rem', fontSize: 13, textDecoration: 'none' }}>＋ One-pager</Link>
        <Link to="/video" className="btn" style={{ padding: '0.35rem 0.7rem', fontSize: 13, textDecoration: 'none' }}>Videos</Link>
        <button type="button" className="btn" onClick={() => setTheme((t) => (t === 'light' ? 'dark' : 'light'))} style={{ padding: '0.35rem 0.7rem', fontSize: 13 }}>
          {theme === 'light' ? '☾ Dark' : '☀ Light'}
        </button>
      </header>

      <main style={{ maxWidth: 1080, margin: '0 auto', padding: '2rem 1.6rem 4rem', display: 'flex', flexDirection: 'column', gap: '2.4rem' }}>
        {/* Videos */}
        <section>
          <SectionHead label="Videos" count={renders.length} hint="Rendered, ready to share" />
          {renders.length === 0 ? (
            <Empty>No rendered videos yet. Open <Link to="/video" className="link">Videos</Link> to render one.</Empty>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '1.1rem' }}>
              {renders.map((r) => (
                <div key={r.file} className="card" style={{ overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                  <video controls preload="metadata" src={r.url} style={{ width: '100%', aspectRatio: '16/9', background: '#000', display: 'block' }} />
                  <div style={{ padding: '0.7rem 0.85rem', display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: 13.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{renderLabel(r.file)}</div>
                      <div className="font-mono" style={{ fontSize: 10.5, color: 'var(--text-dim)' }}>{r.sizeMb} MB · mp4</div>
                    </div>
                    <a href={r.url} download className="btn" style={{ padding: '0.3rem 0.6rem', fontSize: 12, textDecoration: 'none' }}>↓</a>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* One-pagers */}
        <section>
          <SectionHead label="One-pagers" count={docs.length} hint="Click to reopen and keep editing" />
          {docs.length === 0 ? (
            <Empty>No one-pagers yet. Open <Link to="/" className="link">One-pager</Link> to make one.</Empty>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '0.8rem' }}>
              {docs.map((d) => (
                <button key={d.name} type="button" onClick={() => nav(`/?open=${encodeURIComponent(d.name)}`)} className="card"
                  style={{ textAlign: 'left', padding: '0.9rem 1rem', cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: 6, minHeight: 92 }}>
                  <span className="chip" style={{ alignSelf: 'flex-start', background: 'var(--accent-soft)', color: 'var(--accent)' }}>one-pager</span>
                  <span style={{ fontWeight: 600, fontSize: 14, lineHeight: 1.3 }}>{d.title}</span>
                  <span className="font-mono" style={{ fontSize: 10.5, color: 'var(--text-dim)', marginTop: 'auto' }}>v{d.headSeq}</span>
                </button>
              ))}
            </div>
          )}
        </section>

        {/* Video projects (specs) */}
        <section>
          <SectionHead label="Video projects" count={specs.length} hint="Editable specs — open to render or tweak" />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '0.8rem' }}>
            {specs.map((s) => (
              <Link key={s.name} to={`/video/edit/${encodeURIComponent(s.name)}`} className="card"
                style={{ textDecoration: 'none', padding: '0.9rem 1rem', display: 'flex', flexDirection: 'column', gap: 6, minHeight: 92, color: 'var(--text)' }}>
                <span className="chip" style={{ alignSelf: 'flex-start', background: 'var(--video-soft, var(--surface-2))', color: 'var(--moxo-aqua, var(--text-dim))' }}>{s.type}</span>
                <span style={{ fontWeight: 600, fontSize: 14, lineHeight: 1.3 }}>{s.title}</span>
                <span className="font-mono" style={{ fontSize: 10.5, color: 'var(--text-dim)', marginTop: 'auto' }}>{s.scenes} scenes</span>
              </Link>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}

function SectionHead({ label, count, hint }: { label: string; count: number; hint: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: '0.9rem', paddingBottom: '0.5rem', borderBottom: '1px solid var(--border)' }}>
      <h2 className="font-display" style={{ margin: 0, fontSize: 22, fontWeight: 600 }}>{label}</h2>
      <span className="font-mono" style={{ fontSize: 12, color: 'var(--accent)' }}>{count}</span>
      <span style={{ flex: 1 }} />
      <span style={{ fontSize: 12.5, color: 'var(--text-dim)' }}>{hint}</span>
    </div>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return <div style={{ padding: '1.2rem', color: 'var(--text-dim)', fontSize: 13.5, background: 'var(--surface-2)', border: '1px dashed var(--border)', borderRadius: 12 }}>{children}</div>;
}
